import { useCallback, useEffect, useRef, useState } from 'react';
import messagerieSocket from '../socket/messagerieSocket';

export const useMessagerie = ({ userId, enabled = true }) => {
  const [connected, setConnected]     = useState(messagerieSocket.connected);
  const [inbox, setInbox]             = useState([]);
  const [sent, setSent]               = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading]         = useState(false);
  const [error, setError]             = useState(null);
  const [pagination, setPagination]   = useState({ page: 1, total: 0, pages: 1 });
  const cleanupRefs = useRef([]);

  useEffect(() => {
    if (!enabled || !userId) return;

    // Connexion si pas déjà connecté
    if (!messagerieSocket.connected) {
      messagerieSocket.connect();
    }

    const cleanups = cleanupRefs.current;

    // ── Événements système ──────────────────────────────────────────────
    cleanups.push(
      messagerieSocket.on('connected', () => {
        setConnected(true);
        setError(null);
        messagerieSocket.getInbox({ page: 1, limit: 50, isArchived: false });
        messagerieSocket.getUnreadCount();
      }),
      messagerieSocket.on('disconnected', () => setConnected(false)),
      messagerieSocket.on('error', ({ message: msg }) => {
        setError(msg);
        setLoading(false);
      }),
    );

    // ── Inbox ───────────────────────────────────────────────────────────
    cleanups.push(
      messagerieSocket.on('inbox', ({ messages, pagination: pag, unreadCount: uc }) => {
        setInbox(messages || []);
        setPagination(pag || { page: 1, total: 0, pages: 1 });
        if (uc !== undefined) setUnreadCount(uc);
        setLoading(false);
      }),
    );

    // ── Sent ────────────────────────────────────────────────────────────
    cleanups.push(
      messagerieSocket.on('sent', ({ messages }) => {
        setSent(messages || []);
        setLoading(false);
      }),
    );

    // ── Nouveau message temps réel ──────────────────────────────────────
    cleanups.push(
      messagerieSocket.on('new-message', ({ message }) => {
        setInbox(prev => prev.find(m => m._id === message._id) ? prev : [message, ...prev]);
        setUnreadCount(prev => prev + 1);
      }),
    );

    // ── Unread count ────────────────────────────────────────────────────
    cleanups.push(
      messagerieSocket.on('unread-count', ({ count }) => setUnreadCount(count)),
    );

    // ── Mark read ───────────────────────────────────────────────────────
    cleanups.push(
      messagerieSocket.on('message-read', ({ messageId }) => {
        setInbox(prev => prev.map(m => m._id === messageId ? { ...m, status: 'read' } : m));
      }),
    );

    // ── Archive ─────────────────────────────────────────────────────────
    cleanups.push(
      messagerieSocket.on('message-archived', ({ messageId }) => {
        setInbox(prev => prev.filter(m => m._id !== messageId));
      }),
    );

    // ── Message envoyé ──────────────────────────────────────────────────
    cleanups.push(
      messagerieSocket.on('message-sent', ({ message }) => {
        setSent(prev => [message, ...prev]);
        setLoading(false);
      }),
    );

    // ── Réponse envoyée ─────────────────────────────────────────────────
    cleanups.push(
      messagerieSocket.on('reply-sent', () => {
        // Refresh inbox pour avoir le thread à jour
        messagerieSocket.getInbox({ page: 1, limit: 50 });
        setLoading(false);
      }),
    );

    // Si déjà connecté au moment du mount, charger immédiatement
    if (messagerieSocket.connected) {
      setConnected(true);
      messagerieSocket.getInbox({ page: 1, limit: 50, isArchived: false });
      messagerieSocket.getUnreadCount();
    }

    return () => {
      cleanups.forEach(off => off());
      cleanupRefs.current = [];
    };
  }, [enabled, userId]);

  // ── Actions ────────────────────────────────────────────────────────────────

  const markAsRead = useCallback((messageId) => {
    messagerieSocket.markAsRead(messageId);
    setInbox(prev => prev.map(m => m._id === messageId ? { ...m, status: 'read' } : m));
    setUnreadCount(prev => Math.max(0, prev - 1));
  }, []);

  const archiveMessage = useCallback((messageId) => {
    messagerieSocket.archiveMessage(messageId);
  }, []);

  const replyToMessage = useCallback((messageId, body) => {
    if (!body.trim()) return;
    setLoading(true);
    messagerieSocket.replyToMessage(messageId, body);
  }, []);

  const sendMessage = useCallback((to, subject, body, metadata = {}) => {
    if (!to || !subject || !body.trim()) return;
    setLoading(true);
    messagerieSocket.sendMessage({ to, subject, body, messageType: 'conversation', metadata });
  }, []);

  const loadSent = useCallback(() => {
    setLoading(true);
    messagerieSocket.getSent({ page: 1, limit: 50 });
  }, []);

  const loadArchived = useCallback(() => {
    setLoading(true);
    messagerieSocket.getInbox({ page: 1, limit: 50, isArchived: true });
  }, []);

  return {
    connected,
    inbox,
    sent,
    unreadCount,
    loading,
    error,
    pagination,
    markAsRead,
    archiveMessage,
    replyToMessage,
    sendMessage,
    loadSent,
    loadArchived,
  };
};

// ── Badge background (connexion légère pour le compteur seulement) ──────────
export const useMessagrieBadge = ({ userId, legacyUnreadCount = 0 }) => {
  const [socketUnread, setSocketUnread] = useState(0);

  useEffect(() => {
    if (!userId) return;
    if (!messagerieSocket.connected) messagerieSocket.connect();

    const cleanups = [
      messagerieSocket.on('unread-count', ({ count }) => setSocketUnread(count)),
      messagerieSocket.on('new-message', () => setSocketUnread(p => p + 1)),
      messagerieSocket.on('message-read', () => setSocketUnread(p => Math.max(0, p - 1))),
      messagerieSocket.on('connected', () => messagerieSocket.getUnreadCount()),
    ];

    if (messagerieSocket.connected) messagerieSocket.getUnreadCount();

    return () => cleanups.forEach(off => off());
  }, [userId]);

  return socketUnread + legacyUnreadCount;
};